import { addApi } from "aldoo-ra/CMS/field-api-resolver"
import { addFormatter } from "aldoo-ra/CMS/field-formatter"
import { addDisplayFormatter } from "aldoo-ra/CMS/field-display-format-resolver"
import { addFieldEditor } from "aldoo-ra/CMS/field-editor-resolver"
import { addTypeEditor } from "aldoo-ra/CMS/type-editor-resolver"
import { addFieldValidator } from "aldoo-ra/CMS/field-validator-resolver"
import { addCustomAction } from "aldoo-ra/CMS/custom-action-resolver"
import StoreProductAPI from "./api"
import StoreProductFormatter from "./formatters/store-product-formatter"
import openPriceEditorAction from "./custom-actions/price-editor-action"
import { StorePrice } from "../StorePrice"
import { BundleItemType } from "../BundleItem"
import { getStoreProductTypesForCMS } from "aldoo-ra/Admin/Store/Products/store-product-utils"
import { renderProductTypeOption, renderProductTypeSelectedValue } from "aldoo-ra/Admin/Store/Products/product-type-renderer"
import { GetResolverOptionsForCMS } from "aldoo-ra/Store/Availability"
import AvailabilityResolverEditor from "./editors/availability-resolver-editor"
import SupportedPlatformsEditor from "./editors/supported-platforms-editor"
import SeatsEditor from "./editors/seats-editor"
import PIDEditor from "./editors/pid-editor"
import StoreProductContentEditor from "./editors/store-product-content-editor"
import maxLengthValidator from "aldoo-ra/CMS/field-validators/max-length"
import storePIDValidator from "aldoo-ra/CMS/field-validators/store-pid-validator"

export const StoreProduct = {
    type: "StoreProduct",
    formatter: "StoreProduct",
    label: "Store Product Configuration",
    typeEditor: "StoreProductTypeEditor",
    groups: [
        {
            id: "availability",
            label: "Availability",
            description: "Product availability and visibility settings.",
        },
        {
            id: "basic",
            label: "Basic Information",
            description: "Core product information and configuration.",
            expanded: false,
        },
        {
            id: "details",
            label: "Product Details",
            description: "Additional product specifications and settings.",
            visible: false,
        },
        {
            id: "bundle",
            label: "Bundle Configuration",
            description: "Configure bundle contents and pricing",
            visibleIf: {
                type: { in: ["bundle"] }
            }
        },
        {
            id: "pricing",
            label: "Pricing",
            description: "Product pricing configuration.",
        },
        {
            id: "licensing",
            label: "Licensing",
            description: "Product licensing configuration.",
        },
        {
            id: "meta",
            label: "Metadata",
            description: "Additional metadata and custom fields.",
        }
    ],
    fields: [
        {
            id: "type",
            label: "Purchase Type",
            type: "Text",
            required: true,
            default: "purchase",
            description: "The type of product being configured",
            get options() {
                return getStoreProductTypesForCMS()
            },
            onItemRender: renderProductTypeOption,
            onValueItemRender: renderProductTypeSelectedValue,
            render: {
                variant: "admin_highlighted",
                showCheckbox: false,
                showSelectionTick: true,
                enableResetButton: false,
                allowEmptySelection: false
            },
            group: "pricing",
        },
        {
            id: "pid",
            label: "Product ID",
            type: "Text",
            required: true,
            unique: true,
            description: "Unique identifier for the product (e.g., pt-module-princess)",
            group: "details",
            validators: {
                MaxLength: { max: 255 },
                StorePIDValidator: {}
            },
            fieldEditor: "PIDEditor"
        },
        {
            id: "name",
            label: "Product Name",
            type: "Text",
            required: true,
            localization: true,
            description: "The display name of the product on the store (defaults to English)",
            group: "basic",
            render: {
                autoFocus: true
            }
        },
        {
            id: "thumbnail",
            label: "Thumbnail",
            type: "Image",
            description: "Product thumbnail image",
            group: "basic",
            thumbnailSize: "full", // Make thumbnail fill container width
        },
        {
            id: "description",
            label: "Description",
            type: "Text",
            // the description is HTML: it is written in the same editor the mail
            // templates use, and localized like every other CMS field
            fieldEditor: "HtmlEditor",
            localization: true,
            required: false,
            multiline: true,
            description: "Detailed product description (defaults to English)",
            group: "basic",
            render: { variant: "full" },
        },
        {
            id: "category",
            label: "Category",
            type: "Text",
            description: "The category under which the product appears in the store",
            group: "details",
        },
        {
            id: "enabled",
            label: "Enabled",
            type: "Boolean",
            // the one switch that says whether the product sells: the storefront
            // hides it, the purchase path refuses it, and the old readers see it
            // as `availableForPurchase`
            description: "Whether the product is offered to the end-user — off takes it out of the store and refuses new purchases",
            default: true,
            group: "availability",
            render: { variant: "toggle" },
        },
        {
            id: "availability",
            label: "How to check product availability?",
            type: "Text",
            description: "Select the resolvers to determine product availability",
            default: ["in-stock"],
            get options() {
                return GetResolverOptionsForCMS()
            },
            fieldEditor: "AvailabilityResolverEditor",
            group: "availability",
            multiple: false,
        },
        {
            id: "amount",
            label: "Amount",
            type: "Number",
            description: "Value amount (applicable for currency type products)",
            default: 1,
            group: "details",
            conditional: {
                field: "type",
                value: "credits"
            }
        },
        {
            id: "consumable",
            label: "Consumable",
            type: "Boolean",
            description: "Whether the product is consumed immediately instead of being added to inventory",
            group: "details",
        },
        {
            id: "created",
            label: "Created Date",
            type: "DateTime",
            readOnly: true,
            description: "Date when the product was created",
            group: "details",
        },
        {
            id: "updated",
            label: "Last Updated",
            type: "DateTime",
            readOnly: true,
            description: "Date when the product was last modified",
            group: "details",
        },
        {
            id: "price",
            label: "Price Configuration",
            type: "DatabaseModel",
            item: "StorePrice",
            required: false,
            //display formatter
            overview: "priceSearchResult",
            description: "Associated price configuration for the product",
            render: {
                variant: "default",
                hideSelectButton: true,
                hideClearButton: true,
                customEditHandler: "openPriceEditor" // Will use custom action from parent
            },
            group: "pricing",
        },
        {
            id: "subscriptionGroup",
            label: "Subscription Group",
            type: "Text",
            description: "Controls subscription replacement — 'single' groups cancel the existing sub when a new one is purchased",
            group: "pricing",
        },
        {
            id: "licenseModel",
            label: "License Model",
            type: "DatabaseModel",
            item: "ProductLicenseModel",
            searchFields: ["name"],
            overview: "ProductLicenseModelDetailed",
            description: "Optional license model that will be generated when user purchases this product",
            render: { variant: "full" },
            group: "licensing",
        },
        {
            id: "supported_platforms",
            label: "Supported Platforms",
            type: "Array",
            description: "Device platforms that can activate licenses generated from this product",
            default: ["any"],
            options: [
                { value: "any", label: "All Platforms" },
                { value: "ios", label: "iOS" },
                { value: "android", label: "Android" },
                { value: "windows", label: "Windows" },
                { value: "linux", label: "Linux" },
                { value: "macos", label: "macOS" },
                { value: "web", label: "Web" },
            ],
            fieldEditor: "SupportedPlatformsEditor",
            group: "licensing",
        },
        {
            id: "seats",
            label: "License Seats",
            type: "Number",
            description: "Specify custom seats that will overwrite the LicenseModel setting. This allows you to create custom seats when purchasing this product",
            default: 0,
            min: 0,
            fieldEditor: "SeatsEditor",
            group: "licensing",
        },
        {
            id: "meta",
            label: "Metadata",
            type: "Object",
            description: "Additional metadata for app-specific business logic",
            group: "meta",
            render: { variant: "full" },
        },
        {
            id: "bundle",
            label: "Bundle Items",
            type: "Array",
            group: "bundle",
            description: "Products included in this bundle",
            visibleIf: {
                type: { in: ["bundle"] }
            },
            fieldEditor: "BundleItemEditor",
            item: BundleItemType,
            render: { variant: "full" },
        },
    ]
};

//Export Store Product API
addApi("StoreProduct", StoreProductAPI)
addFormatter("StoreProduct", StoreProductFormatter)

// Register custom field editors
addFieldEditor("AvailabilityResolverEditor", AvailabilityResolverEditor)
addFieldEditor("SupportedPlatformsEditor", SupportedPlatformsEditor)
addFieldEditor("SeatsEditor", SeatsEditor)
addFieldEditor("PIDEditor", PIDEditor)

// Register custom type editor
addTypeEditor("StoreProductTypeEditor", StoreProductContentEditor)

// Register field validators
addFieldValidator("MaxLength", maxLengthValidator)
addFieldValidator("StorePIDValidator", storePIDValidator)


addDisplayFormatter("StoreProduct", (item) => {
    if (!item || !item.price) return "No price configuration"

    let price = item.price
    let primaryPrice = price.price_model?.find((price) => price.primary)

    if (!primaryPrice) return "No price configuration"
    const purchaseType = parseInt(primaryPrice.duration)
        ? "Subscription"
        : "Purchase"

    return `${item.name} ${purchaseType}`
})

addDisplayFormatter("supportedPlatformsFormatter", (value) => {
    if (!value || !Array.isArray(value)) return "No platforms"
    if (value.includes("any") || value.length === 0) return "All Platforms"
    
    const platformLabels = value.map(platform => {
        switch(platform) {
            case "ios": return "iOS"
            case "android": return "Android" 
            case "windows": return "Windows"
            case "linux": return "Linux"
            case "macos": return "macOS"
            case "web": return "Web"
            default: return platform.charAt(0).toUpperCase() + platform.slice(1)
        }
    })
    return platformLabels.join(", ")
})

// Register custom actions
addCustomAction("StoreProduct.price.openPriceEditor", openPriceEditorAction)

// Register this type in DatabaseModelTypeResolver for dynamic access
export const RegisterInDBResolver = true